// tracking for react router
// TODO: These are built into cached libraries but need to be imported here
//   so they're initilized for other modules. We should update those modules
//   to require these dependencies themselves.

import 'base/lib/handlebar_helpers'

import $ from 'jquery'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import Cookies from 'js-cookie'
import includes from 'lodash/includes'
import 'jquery.velocity.ui.everlane'

import Event from 'base/event'
import Hub from 'base/hub'
import setupInitializers from 'base/initializers/all'
import initializeSpeedcurve from 'base/initializers/speedcurve'
import land from 'base/lib/land'
import setupTrackers from 'base/lib/trackers/all'
import bootstrappedData from 'base/lib/singletons/bootstrapped_data'

import browserRequirements from 'base/lib/browser_requirements'
import ScrollDepthTracker from 'base/lib/scroll_depth_tracker'

import store from 'base/store'
import * as asyncActions from 'base/actions/asynchronous'
import * as syncActions from 'base/actions/synchronous'
import csrfToken from 'base/lib/csrf_token'
import { EVERLANE_USER as EVERLANE_USER_COOKIE_NAME } from 'cookies.yml'
import { fireGTMSession } from 'base/lib/trackers/google_tag_manager'
import { detectSafari } from 'base/lib/device'

/*
 * NOTE:
 * If a Feature's `activate` returns a promise,
 * init will wait until that promise finishes.
 */
class Feature {
  constructor(name, activate) {
    this.name = name
    this.activate = activate
  }
}

// Initialize the core of the frontend desktop or mobile experience before
// passing control back to the entry point of that experience.
//
// Sets up the following things:
//   - Fetching site menus
//   - Setting up the Session
//   - Running all the `base/initializers`
//   - Publishing the `LAND` and `Cart.INIT` events
//   - Loading the deferred modules
//   - Exposing some tools for employees
function init({ skipFeatures = [] } = {}) {
  function getMetaContent(name) {
    return document.querySelector(`meta[name=${name}]`)?.content
  }

  const features = [
    new Feature('bugsnag', () => {
      // See app/views/application/_bugsnag.html.erb
      const config = JSON.parse(getMetaContent('bugsnag-config'))

      Bugsnag.start({
        ...config,
        appType: 'client',
        plugins: [new BugsnagPluginReact()],

        onError(event) {
          const state = store.getState()

          event.addMetadata('session', {
            _csrf_token: csrfToken(),
            mobile_site: !!state?.appConfig?.isMobile,
            session_id: state?.session?.visitor?.session_id,
          })

          event.setUser(state?.session?.user?.id)
        },
      })
    }),

    new Feature('appMenus', () => {
      store.dispatch(asyncActions.fetchSidenavMenus())
    }),

    new Feature('appConfig', () => {
      return store.dispatch(asyncActions.fetchAppConfig())
    }),

    new Feature('shippingConstants', () => {
      store.dispatch(asyncActions.fetchShippingConstants())
    }),

    new Feature('sessionAndGeolocateVisitorAndCart', async () => {
      const session = await store.dispatch(asyncActions.fetchSession())
      const urlParams = new URLSearchParams(window.location.search)
      let localeOverride = urlParams.get('locale')
      // simple protection against unexpected data in the locale param
      if (localeOverride?.length !== 2) {
        localeOverride = null
      }

      store.dispatch(asyncActions.geolocateVisitor(localeOverride))
      store.dispatch(asyncActions.fetchCart(null, true)) // second arg true skips cache

      // If the user is in the nav redesign treatment, disregard the international menus
      await store.dispatch(asyncActions.fetchMenus({ isIntl: false, version: 2 }))

      // Initialize SpeedCurve after user ID and gender are available, if set
      initializeSpeedcurve()
      // Set global session and user id variables for GTM
      fireGTMSession()

      return session
    }),

    new Feature('showJqxhrErrors', () => {
      $.Deferred.exceptionHook = error => {
        if (console && console.error) {
          console.error(error)
        }
      }
    }),

    // HACK: definitely remove this when we are doing routing through react-router.
    // This allows us to keep the store in sync with the URL. By doing so, components can react
    // easily to URL changes since they will re-render upon this action being dispatched
    new Feature('connectURLToRedux', () => {
      const updatePath = () => {
        store.dispatch(
          syncActions.updateCurrentPath(
            window.location.pathname,
            window.location.hash,
            window.location.search,
          ),
        )
      }

      Hub.sub(Event.App.ROUTE, (_event, { previousRoute }) => {
        if (previousRoute) {
          updatePath()
        }
      })
    }),

    new Feature('showHireMeMessage', () => {
      store.dispatch(syncActions.showHireMe())
    }),

    new Feature('csrfProtection', () => {
      $.ajaxSetup({
        // Don't timestamp AJAX requests - let them be cached.
        cache: true,

        beforeSend(xhr, setting) {
          // Add X-CSRF-Token only when url is the same origin.
          const url = new URL(setting.url, window.location.origin)
          if (url.origin === window.location.origin) {
            // Include CSRF headers so we get CSRF protection. Use the latest
            // value from cookies at it will change over time.
            xhr.setRequestHeader('X-CSRF-Token', csrfToken())
          }
        },
      })
    }),

    new Feature('initializers', () => {
      setupInitializers()
    }),

    new Feature('trackers', () => {
      setupTrackers()
      land()
    }),

    new Feature('repeatUsers', () => {
      if (Cookies.get(EVERLANE_USER_COOKIE_NAME)) {
        Hub.pub(Event.App.REPEAT_USER_LAND)
      }
    }),

    new Feature('scrollDepthTracker', () => {
      Hub.sub(Event.App.ROUTE, (_event, { path }) => {
        const previousScrollState = ScrollDepthTracker.getPrevious()

        if (!previousScrollState || previousScrollState.path !== path) {
          window.scroll(0, 0)
          return
        }

        $('.viewport').velocity('scroll', {
          offset: previousScrollState.scrollDepth,
        })
      })
    }),

    // Triggers the 'load' dependency when the window is loaded and the
    // 'postCritical' dependency when the critical loading stuff is done and
    // the site is interactable.
    new Feature('dependentEvents', () => {
      window.addEventListener('load', () => {
        Hub.pub(Event.App.LOAD)
        //DependentEvents.fulfill('load')
      })

      let seenFirstRoute = false
      Hub.sub(Event.App.ROUTE, () => {
        if (!seenFirstRoute) {
          seenFirstRoute = true
          Hub.pub(Event.App.POST_CRITICAL)
          //DependentEvents.fulfill('postCritical')
        }
      })

      let userInteracted = false

      const events = ['scroll', 'mousedown', 'mousemove', 'click', 'keydown', 'touchstart']
      const callback = () => {
        if (!seenFirstRoute) {
          return null
        }

        userInteracted = true
        Hub.pub(Event.App.FIRST_INTERACTION)
        //DependentEvents.fulfill('afterUserInteract')
        events.forEach(event => window.removeEventListener(event, callback))
      }

      if (!userInteracted) {
        events.forEach(event => window.addEventListener(event, callback))
      }
    }),

    // Be very careful about what you expose here!
    new Feature('exposeTools', () => {
      window.Tools = {
        bootstrappedData,
        Hub,
        Event,
        browserRequirements,
        store,
        syncActions,
        asyncActions,
      }

      window.Tools.setGeolocation = country_code => {
        store.dispatch(asyncActions.changeGeolocation(country_code))
      }
    }),

    new Feature('WebCryptoPolyfill', async () => {
      await import('webcrypto-shim')
    }),

    new Feature('DateInputPolyfill', async () => {
      if (detectSafari()) {
        await import('date-input-polyfill-react')
      }
    }),
  ]

  // Iterate over the features in order and activate them
  return Promise.all(
    features.map(feature => {
      return includes(skipFeatures, feature.name) ? Promise.resolve(null) : feature.activate()
    }),
  )
}

export default init
